import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ShieldAlert } from "lucide-react";

export default function ForgotPasswordModal({ open, onClose }) {
  const [step, setStep] = useState(1); // 1=request, 2=reset
  const [username, setUsername] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(1); setUsername(""); setRecoveryEmail("");
    setResetToken(""); setNewPassword(""); setBusy(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const requestToken = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        username: username.trim(),
        recovery_email: recoveryEmail.trim(),
      });
      if (data.reset_token) {
        setResetToken(data.reset_token);
        setStep(2);
        toast.success("Reset code generated");
      } else {
        toast.message(data.message || "Check backend logs for reset code");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, new_password: newPassword });
      toast.success("Password reset. Log in with your new password.");
      handleClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(resetToken);
    toast.success("Code copied");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="bg-[#0E131F] border border-slate-800 chamfered max-w-md" data-testid="forgot-password-modal">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wide text-amber-500 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" /> Password Recovery
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <form onSubmit={requestToken} className="space-y-4">
            <p className="text-sm text-slate-400 font-mono-vrax">
              Enter the shared username and the recovery email set on file. A one-time reset code will be generated.
            </p>
            <div>
              <label className="label-mono block mb-1">Username</label>
              <input
                required
                className="input-tactical w-full px-3 py-2 rounded"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid="forgot-username-input"
              />
            </div>
            <div>
              <label className="label-mono block mb-1">Recovery Email</label>
              <input
                required
                type="email"
                className="input-tactical w-full px-3 py-2 rounded"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                data-testid="forgot-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-signal w-full py-2.5 rounded"
              data-testid="forgot-submit-btn"
            >
              {busy ? "Generating…" : "Generate Reset Code"}
            </button>
          </form>
        ) : (
          <form onSubmit={resetPassword} className="space-y-4">
            <div>
              <label className="label-mono block mb-1">Reset Code</label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={resetToken}
                  className="input-tactical w-full px-3 py-2 rounded text-xs"
                  data-testid="reset-token-display"
                />
                <button
                  type="button"
                  onClick={copyToken}
                  className="btn-ghost-tactical px-3 rounded"
                  data-testid="copy-reset-token"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1 font-mono-vrax">
                // Also logged in backend console. Valid for 1 hour.
              </p>
            </div>
            <div>
              <label className="label-mono block mb-1">New Password</label>
              <input
                required
                minLength={6}
                type="password"
                className="input-tactical w-full px-3 py-2 rounded"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-signal w-full py-2.5 rounded"
              data-testid="reset-password-submit-btn"
            >
              {busy ? "Resetting…" : "Reset Password"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
